import { useCallback, useMemo } from 'react';
import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
} from '@mui/material';
import { Listbox } from '../../components/listbox';
import { withControl } from '../../hoc/withControl';
import { TypePointGate, typePointQuery } from './model';
import type { GetTypePointPayload, Value } from './types';

type Props = GetTypePointPayload & {
  placeholder?: string;
  disableClearable?: boolean;
};

export const TypePointControl = withControl<Props>(
  ({
    label,
    value,
    setValue,
    error,
    required,
    readOnly,
    disabled,
    isError,
    disableClearable,
    placeholder,
  }) => {
    useGate(TypePointGate);
    const { data, pending } = useUnit(typePointQuery);

    const mapIds = (values: Value[]) => values.map(({ id }) => id);
    const handleChange = (_: unknown, newValue: Value[]) => setValue(mapIds(newValue));

    const options = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const getValue = useCallback(
      () =>
        options.filter(
          ({ id }) =>
            value?.includes(id) || value?.find((group: Value) => group?.id === id)
        ),
      [options, value]
    );

    const values = getValue();

    return (
      <FormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          multiple
          size="small"
          disableClearable={disableClearable}
          loading={pending}
          value={values}
          options={options}
          onChange={handleChange}
          ListboxComponent={Listbox}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                error: isError,
                readOnly,
                placeholder: !readOnly && values.length === 0 ? placeholder : undefined,
                endAdornment: readOnly ? null : (
                  <>
                    {pending && <CircularProgress size={20} color="inherit" />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
